import React, { useState } from 'react';
import { Receipt, Building2, User, Mail, MapPin, Briefcase, Copy, Check } from 'lucide-react';
import toast from 'react-hot-toast';

/**
 * Componente informativo para mostrar la ficha de Facturación / Boleta
 * en las vistas administrativas (Gestión de Solicitudes, Historial, Trámites).
 */
export default function FacturacionCardInfo({ data, isDark = false, style = {} }) {
  const [copied, setCopied] = useState(false);

  if (!data) return null;

  // Determinar si fue emitida al mismo solicitante o a un tercero/empresa
  const esMismaPersona = data.facturacion_misma_persona === true || 
    (data.facturacion_misma_persona !== false && !data.boleta_razon_social && !data.boleta_giro);

  const rutBoleta = data.boleta_rut || data.rut_boleta || data.rut_solicitante || '—';
  const razonSocial = data.boleta_razon_social || data.nombre_boleta || data.nombre_solicitante || '—';
  const giro = data.boleta_giro || '—';
  const correo = data.boleta_correo || data.email || data.correo || '—';
  const direccion = data.boleta_direccion || data.direccion || '—';

  const handleCopy = () => {
    const text = [
      `RUT: ${rutBoleta}`,
      `Razón Social / Nombre: ${razonSocial}`,
      giro !== '—' ? `Giro: ${giro}` : null,
      `Correo Facturación: ${correo}`,
      direccion !== '—' ? `Dirección: ${direccion}` : null
    ].filter(Boolean).join('\n');

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Datos de facturación copiados');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div style={{
      background: isDark ? 'rgba(255, 255, 255, 0.03)' : '#ffffff',
      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'}`,
      borderRadius: '10px',
      padding: '1rem 1.25rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
      ...style
    }}>
      {/* Encabezado */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: '0.85rem',
        paddingBottom: '0.65rem',
        borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : '#f1f5f9'}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '28px',
            height: '28px',
            borderRadius: '6px',
            background: isDark ? 'rgba(2, 132, 199, 0.2)' : '#e0f2fe',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Receipt size={16} color="var(--primary, #0284c7)" />
          </div>
          <div>
            <h4 style={{
              margin: 0,
              fontSize: '0.92rem',
              fontWeight: 700,
              color: isDark ? '#f8fafc' : '#0f172a'
            }}>
              Datos de Facturación / Boleta
            </h4>
            <span style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#64748b' }}>
              Información tributaria legal para emisión
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{
            fontSize: '0.72rem',
            fontWeight: 600,
            padding: '3px 9px',
            borderRadius: '12px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            background: esMismaPersona
              ? (isDark ? 'rgba(59, 130, 246, 0.15)' : '#eff6ff')
              : (isDark ? 'rgba(16, 185, 129, 0.15)' : '#ecfdf5'),
            color: esMismaPersona
              ? (isDark ? '#60a5fa' : '#1d4ed8')
              : (isDark ? '#34d399' : '#047857'),
            border: `1px solid ${esMismaPersona
              ? (isDark ? 'rgba(59, 130, 246, 0.3)' : '#bfdbfe')
              : (isDark ? 'rgba(16, 185, 129, 0.3)' : '#a7f3d0')}`
          }}>
            {esMismaPersona ? <User size={12} /> : <Building2 size={12} />}
            {esMismaPersona ? 'Emisión a Solicitante' : 'Emisión a Tercero / Empresa'}
          </span>

          <button
            type="button"
            onClick={handleCopy}
            title="Copiar datos al portapapeles"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 10px',
              fontSize: '0.75rem',
              fontWeight: 600,
              borderRadius: '6px',
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.15)' : '#cbd5e1'}`,
              background: isDark ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
              color: isDark ? '#e2e8f0' : '#334155',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            {copied ? <Check size={13} color="#10b981" /> : <Copy size={13} />}
            {copied ? 'Copiado' : 'Copiar'}
          </button>
        </div>
      </div>

      {/* Grid de campos */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '0.85rem'
      }}>
        <div>
          <span style={{ display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600, letterSpacing: '0.3px', marginBottom: '2px' }}>
            RUT Boleta / Empresa
          </span>
          <span style={{ fontSize: '0.88rem', fontWeight: 700, color: isDark ? '#ffffff' : '#0f172a', fontFamily: 'monospace' }}>
            {rutBoleta}
          </span>
        </div>

        <div>
          <span style={{ display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600, letterSpacing: '0.3px', marginBottom: '2px' }}>
            Razón Social o Nombre
          </span>
          <span style={{ fontSize: '0.88rem', fontWeight: 600, color: isDark ? '#e2e8f0' : '#1e293b' }}>
            {razonSocial}
          </span>
        </div>

        <div>
          <span style={{ display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600, letterSpacing: '0.3px', marginBottom: '2px' }}>
            Giro Comercial
          </span>
          <span style={{ fontSize: '0.85rem', color: isDark ? '#cbd5e1' : '#334155' }}>
            {giro}
          </span>
        </div>

        <div>
          <span style={{ display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600, letterSpacing: '0.3px', marginBottom: '2px' }}>
            Correo de Facturación
          </span>
          <a
            href={`mailto:${correo}`}
            style={{ fontSize: '0.85rem', color: 'var(--primary, #0284c7)', textDecoration: 'none', fontWeight: 500 }}
          >
            {correo}
          </a>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <span style={{ display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600, letterSpacing: '0.3px', marginBottom: '2px' }}>
            Dirección Tributaria
          </span>
          <span style={{ fontSize: '0.85rem', color: isDark ? '#cbd5e1' : '#334155' }}>
            {direccion}
          </span>
        </div>
      </div>
    </div>
  );
}
